import React, { useState, useRef, createRef } from "react";
import {
    Tabs,
    Button,
    Modal,
    Skeleton,
    Card,
    Space,
    Typography
} from "antd";
import {
    FilePdfOutlined,
    CheckCircleOutlined,
    EditOutlined,
    SendOutlined,
    PrinterOutlined
} from "@ant-design/icons";
import DTRFFormat from "../../components/docs/DTRFFormat";
import DTRFFormatProposition from "../../components/docs/DTRFFormatProposition";
import DTRFWPPFormat from "../../components/docs/DTRFWPPFormat";
import DTRFWPPFormatToPrint from "../../components/docs/DTRFWPPFormatToPrint";
import AttestationMotoToPrint from "../../components/docs/AttestationMotoToPrint";
import MouvementsContent from "./MouvementContent";
import ImprimerAttestation from "./ImprimerAttestation";
import ImprimerAttestationCEDEAO from "./ImprimerAttestationCEDEAO";

const { Title, Text } = Typography;

const DetailsTab = ({
    contrat,
    reglements,
    loading,
    regle,
    printingCP,
    loadingPrinting,
    printReference,
    printRefContrat,
    printRefAttestation,
    printedAttestation,
    printedCEDEAOAttestation,
    contratModal,
    modalAttOpen,
    modalCEDEAOAttOpen,
    numeroAttestation,
    attestationDamaged,
    printingAttestation,
    printAttestation,
    printingCEDEAOAttestation,
    pdfPreviewUrl,
    tabId,
    onTabsChange,
    setModalAttOpen,
    setModalCEDEAOAttOpen,
    setNumeroAttestation,
    setAttestationDamaged,
    setPrintingAttestation,
    setPrintingCEDEAOAttestation,
    setContratModal,
    exportToPdf,
    exportToOnePdf,
    showContractValidationConfirm,
    reglementWarningWithConfirmAndCancelMessage,
    attestationPrinting,
    attestationDamagedPrinting,
    attestationNonPrintedPrinting,
    attestationCEDEAOPrinting,
    attestationCEDEAODamagedPrinting,
    attestationCEDEAONonPrintedPrinting,
    printingDamagedAttestation,
    printingDamagedCEDEAOAttestation,
    rightsService,
    navigate,
    setLayout,
    showAttestationCEDEAO
}) => {

    const primaryColor = "#1890ff";
    const secondaryColor = "#242166";
    const accentColor = "#52c41a";

    const cardStyle = {
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        border: `1px solid ${secondaryColor}15`
    };

    const buttonStyle = {
        borderRadius: "8px",
        height: "40px",
        fontWeight: 500,
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
    };

    const tabItems = [
        {
            key: "1",
            label: (
                <Space>
                    <FilePdfOutlined />
                    <Text strong>Contrat</Text>
                </Space>
            ),
            children: (
                <Card style={cardStyle} bodyStyle={{ padding: "32px" }}>
                    <div style={{
                        display: "flex",
                        justifyContent: "center",
                        marginBottom: "32px",
                        background: "#fafafa",
                        padding: "24px",
                        borderRadius: "8px"
                    }}>
                        <div style={{ width: "210mm", height: "auto" }}>
                            {contrat && (contrat?.statutContrat === "EN_PROPOSITION" ? (
                                <DTRFFormatProposition contrat={contrat} ref={printReference} />
                            ) : (contrat?.engins?.[0]?.categorie?.codeCategorie === "M" &&
                                contrat?.organime?.typeOrganisme === "POOL" ?
                                !printingCP ?
                                    <DTRFWPPFormat contrat={contrat} ref={printReference} /> :
                                    <DTRFWPPFormatToPrint contrat={contrat} ref={printReference} /> :
                                <DTRFFormat contrat={contrat} ref={printReference} />
                            ))}
                        </div>
                    </div>

                    <Space wrap size="large" style={{ justifyContent: "center", width: "100%" }}>
                        {contrat?.statutContrat === "EN_PROPOSITION" ? (
                            <>
                                <Button
                                    onClick={showContractValidationConfirm}
                                    type="primary"
                                    icon={<CheckCircleOutlined />}
                                    disabled={!rightsService("HCO003")}
                                    style={{
                                        ...buttonStyle,
                                        background: accentColor,
                                        borderColor: accentColor
                                    }}
                                    size="large"
                                >
                                    Valider le Contrat
                                </Button>
                                <Button
                                    onClick={() => navigate(`/editer-contrat-new/${contrat?.codeId}`)}
                                    icon={<EditOutlined />}
                                    disabled={!rightsService("HCO003")}
                                    style={buttonStyle}
                                    size="large"
                                >
                                    Modifier
                                </Button>
                                <Button
                                    loading={loadingPrinting}
                                    onClick={exportToPdf}
                                    type="primary"
                                    icon={<PrinterOutlined />}
                                    disabled={!rightsService("HLA044")}
                                    style={{
                                        ...buttonStyle,
                                        background: secondaryColor,
                                        borderColor: secondaryColor
                                    }}
                                    size="large"
                                >
                                    Imprimer la Proposition
                                </Button>
                            </>
                        ) : regle ? (
                            <Button
                                loading={loadingPrinting}
                                onClick={exportToPdf}
                                type="primary"
                                icon={<FilePdfOutlined />}
                                disabled={!rightsService("HCO006")}
                                style={{
                                    ...buttonStyle,
                                    background: accentColor,
                                    borderColor: accentColor
                                }}
                                size="large"
                            >
                                Imprimer le Contrat
                            </Button>
                        ) : contrat?.avenant?.code?.startsWith("AN") ? (
                            <Button
                                onClick={() => navigate(`/editer-contrat/${contrat?.codeId}`)}
                                icon={<EditOutlined />}
                                disabled={!rightsService("HCO003")}
                                style={buttonStyle}
                                size="large"
                            >
                                Modifier le Contrat
                            </Button>
                        ) : null}

                        {contrat?.organisme?.typeOrgainsme === "POOL" && (
                            <Button
                                onClick={() => setContratModal(true)}
                                type="primary"
                                icon={<SendOutlined />}
                                disabled={(regle && !rightsService("HCO006")) || (!regle && !rightsService("HLA037"))}
                                style={{
                                    ...buttonStyle,
                                    background: primaryColor,
                                    borderColor: primaryColor
                                }}
                                size="large"
                            >
                                Envoyer les Documents
                            </Button>
                        )}
                    </Space>
                </Card>
            )
        },
        {
            key: "2",
            label: (
                <Space>
                    <FilePdfOutlined />
                    <Text strong>Mouvements</Text>
                </Space>
            ),
            children: (
                <MouvementsContent
                    contrat={contrat}
                    reglements={reglements}
                    rightsService={rightsService}
                />
            )
        },
        {
            key: "3",
            label: (
                <Space>
                    <FilePdfOutlined />
                    <Text strong>Attestation</Text>
                </Space>
            ),
            children: null // Géré par le modal
        },
        {
            key: "4",
            label: (
                <Space>
                    <FilePdfOutlined />
                    <Text strong>Carte Grise</Text>
                </Space>
            ),
            children: (
                <Card style={cardStyle} bodyStyle={{ padding: "32px" }}>
                    {contrat?.engins?.[0]?.carteGrise ? (
                        <div style={{ height: "750px", borderRadius: "8px", overflow: "hidden" }}>
                            <embed
                                src={pdfPreviewUrl}
                                type="application/pdf"
                                style={{ width: "100%", height: "100%" }}
                            />
                        </div>
                    ) : (
                        <div style={{
                            textAlign: "center",
                            padding: "60px",
                            color: "#999"
                        }}>
                            <Text type="secondary">Pas de carte grise jointe au contrat</Text>
                        </div>
                    )}
                </Card>
            )
        }
    ];

    if (showAttestationCEDEAO) {
        tabItems.push({
            key: "5",
            label: (
                <Space>
                    <FilePdfOutlined />
                    <Text strong>Attestation CEDEAO</Text>
                </Space>
            ),
            children: null // Géré par le modal
        });
    }

    return (
        <>
            <Skeleton loading={loading} active>
                <Tabs
                    onChange={onTabsChange}
                    items={tabItems}
                    activeKey={tabId}
                    size="large"
                    style={{
                        background: "white",
                        padding: "24px",
                        borderRadius: "12px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
                    }}
                    tabBarStyle={{
                        borderBottom: `2px solid ${secondaryColor}20`,
                        marginBottom: "24px"
                    }}
                />
            </Skeleton>

            {/* Modal Attestation */}
            <ImprimerAttestation
                modalAttOpen={modalAttOpen}
                contrat={contrat}
                onTabsChange={onTabsChange}
                setModalAttOpen={setModalAttOpen}
                printingAttestation={printingAttestation}
                setPrintingAttestation={setPrintingAttestation}
                printedAttestation={printedAttestation}
                printAttestation={printAttestation}
                numeroAttestation={numeroAttestation}
                setNumeroAttestation={setNumeroAttestation}
                attestationDamaged={attestationDamaged}
                setAttestationDamaged={setAttestationDamaged}
                attestationPrinting={attestationPrinting}
                attestationDamagedPrinting={attestationDamagedPrinting}
                attestationNonPrintedPrinting={attestationNonPrintedPrinting}
                printingDamagedAttestation={printingDamagedAttestation}
                regle={regle}
                loadingPrinting={loadingPrinting}
                rightsService={rightsService}
                setLayout={setLayout}
            />

            {/* Modal CEDEAO */}
            {showAttestationCEDEAO && (
                <ImprimerAttestationCEDEAO
                    modalCEDEAOAttOpen={modalCEDEAOAttOpen}
                    contrat={contrat}
                    onTabsChange={onTabsChange}
                    setModalCEDEAOAttOpen={setModalCEDEAOAttOpen}
                    printingCEDEAOAttestation={printingCEDEAOAttestation}
                    setPrintingCEDEAOAttestation={setPrintingCEDEAOAttestation}
                    printedCEDEAOAttestation={printedCEDEAOAttestation}
                    printingDamagedCEDEAOAttestation={printingDamagedCEDEAOAttestation}
                    numeroAttestation={numeroAttestation}
                    setNumeroAttestation={setNumeroAttestation}
                    attestationCEDEAOPrinting={attestationCEDEAOPrinting}
                    attestationCEDEAODamagedPrinting={attestationCEDEAODamagedPrinting}
                    attestationCEDEAONonPrintedPrinting={attestationCEDEAONonPrintedPrinting}
                    attestationDamaged={attestationDamaged}
                    setAttestationDamaged={setAttestationDamaged}
                    regle={regle}
                    rightsService={rightsService}
                />
            )}

            {/* Modal Documents Pool */}
            <Modal
                title={
                    <Title level={4} style={{ margin: 0, color: secondaryColor }}>
                        <SendOutlined style={{ marginRight: 8 }} />
                        Envoyer les Documents
                    </Title>
                }
                open={contratModal}
                onCancel={() => setContratModal(false)}
                footer={null}
                width={1200}
                centered
                bodyStyle={{ padding: "24px" }}
                zIndex={2000}
            >
                <Card style={{ ...cardStyle, marginBottom: 24 }}>
                    <div style={{ background: "#fafafa", padding: "24px", borderRadius: "8px" }}>
                        <div style={{ width: "210mm", height: "297mm", marginBottom: 24 }}>
                            <DTRFWPPFormatToPrint ref={printRefContrat} contrat={contrat} />
                        </div>
                        <div ref={printRefAttestation} style={{ width: "297mm", height: "210mm" }}>
                            <AttestationMotoToPrint
                                engin={contrat?.engins?.[0]}
                                contrat={contrat}
                            />
                        </div>
                    </div>
                </Card>
                <Button
                    onClick={exportToOnePdf}
                    type="primary"
                    icon={<SendOutlined />}
                    loading={loadingPrinting}
                    block
                    style={{
                        ...buttonStyle,
                        background: accentColor,
                        borderColor: accentColor,
                        height: "48px"
                    }}
                    size="large"
                >
                    Envoyer les Documents
                </Button>
            </Modal>
        </>
    );
};

export default DetailsTab;
